/**
 * documentRepositoryUrlState.js — historia 170 (Repositorio Documental), J0. Criterio de búsqueda del
 * repositorio <-> query string de `/configuration/documentRepository` (CA4.3, CU8 paso 2: "atrás"
 * restaura texto/filtros/orden/página/tamaño). Pure functions, no React/React Router import.
 *
 * D10 (R2 recibe `RequestPagination`, ver `documentRepositoryApi.js`): Sun ya no resuelve tipo/
 * extensión/subido por/temática contra conjuntos exactos (ids/valores) — todo es ahora subcadena
 * resuelta en el propio Sun (`columnFilters`) salvo temática (`mixedColumnFilters`, literal). Por eso
 * el criterio de esta pantalla ya NO guarda listas resueltas (`topics`/`documentTypeIds`/`extensions`/
 * `createdUsers`) sino el TEXTO TAL CUAL escrito en cada filtro de columna (`*Filter`, un string, nunca
 * un array) — exactamente lo que `SmartMateriaUITable`'s `column.filter` necesita para re-sembrar la
 * caja al montar/remontar (S94/S95), sin ninguna traducción intermedia. La única excepción es
 * `topicFilter`: sigue guardando el TEXTO (la etiqueta traducida tal cual la escribió el usuario), no
 * los literales `DocumentTopic` — esos solo se resuelven en `documentRepositoryApi.js`, al construir el
 * cuerpo de R2 (frontera de red), nunca aquí.
 *
 * `sortKey` es ahora el `field` de la columna (p. ej. `'createdDate'`, no `'CREATED_DATE'`): D10 deja
 * que Sun traduzca el nombre de campo él mismo (`encargo-sun-r2-request-pagination.md`), así que Jupiter
 * ya no necesita su propio mapa field->enum. `sortDirection` es ahora el propio `1`/`-1` de
 * `requestPagination.sortMode` (nunca `'ASC'`/`'DESC'`) — mismo campo, sin una capa de traducción que ya
 * no aporta nada.
 *
 * Ruptura de compatibilidad declarada (D10, "adapta el criterio de la URL si cambia el modelo"): un
 * enlace/marcador ANTERIOR a esta refactorización que llevara `topics=`/`documentTypeIds=`/
 * `extensions=`/`createdUsers=` (listas) deja de reconocerse — esos parámetros, al no existir ya en
 * `DEFAULT_CRITERIA`, se ignoran en silencio (ni error ni comportamiento sorprendente: la pantalla
 * simplemente arranca sin ese filtro concreto, como si nunca se hubiera escrito). Mismo criterio para
 * `sortKey=CREATED_DATE`/`sortDirection=DESC` (el enum/ASC-DESC antiguos): un enlace viejo con esos
 * valores no coincide con ningún `field`/número válido y la pantalla cae en su propio default
 * (`createdDate`/`-1`), no en un error.
 *
 * `DEFAULT_CRITERIA` is the exact default criteria of R2 (S20): no text, no filters, page 0, size 10,
 * ordered by `createdDate DESC` (`-1`). Here, in the criteria itself, "no date"/"no filter" is always
 * `''`, exactly like every other absent scalar filter (`text`, `recordTopic`, `recordReferenceId`) —
 * CLAUDE.md, no `null`/`undefined` in business logic; the translation to R2's own sentinels happens
 * ONLY at `documentRepositoryApi.js` (the API boundary).
 */

export const DEFAULT_CRITERIA = Object.freeze({
  text: '',
  documentNameFilter: '',
  fileNameFilter: '',
  descriptionFilter: '',
  documentTypeFilter: '',
  extensionFilter: '',
  createdUserFilter: '',
  topicFilter: '',
  recordTopic: '',
  recordReferenceId: '',
  createdFromMillis: '',
  createdToMillis: '',
  page: 0,
  size: 10,
  sortKey: 'createdDate',
  sortDirection: -1
})

const NUMBER_FIELDS_OR_BLANK = ['createdFromMillis', 'createdToMillis']
const STRING_FIELDS = [
  'text', 'documentNameFilter', 'fileNameFilter', 'descriptionFilter', 'documentTypeFilter', 'extensionFilter',
  'createdUserFilter', 'topicFilter', 'recordTopic', 'recordReferenceId', 'sortKey'
]

/** Query string (without the leading "?") -> full criteria, defaults filled for whatever is absent. */
export const criteriaFromSearch = (search) => {
  const params = new URLSearchParams(search || '')
  const criteria = { ...DEFAULT_CRITERIA }

  STRING_FIELDS.forEach((field) => {
    if (params.has(field)) criteria[field] = params.get(field)
  })

  NUMBER_FIELDS_OR_BLANK.forEach((field) => {
    if (!params.has(field)) return
    const raw = params.get(field)
    criteria[field] = raw === '' ? '' : Number(raw)
  })

  if (params.has('page')) criteria.page = Number(params.get('page'))
  if (params.has('size')) criteria.size = Number(params.get('size'))
  if (params.has('sortDirection')) criteria.sortDirection = Number(params.get('sortDirection'))

  return criteria
}

/** Full criteria -> compact query string: only fields that differ from the default are written. */
export const searchFromCriteria = (criteria) => {
  const params = new URLSearchParams()

  Object.keys(DEFAULT_CRITERIA).forEach((field) => {
    const value = criteria[field]
    const defaultValue = DEFAULT_CRITERIA[field]
    if (value === defaultValue) return
    params.set(field, String(value))
  })

  return params.toString()
}

// The fields RN32 treats as "search criteria" — page/size/sort are navigation, not a filter, so they
// never make CU1 A2's empty screen look like CU2 A1's "no match" (S26 vs. CU2 A1, out of J1 scope but
// the distinction is drawn here so both can share this one predicate).
const CRITERIA_FIELDS = [
  'text', 'documentNameFilter', 'fileNameFilter', 'descriptionFilter', 'documentTypeFilter', 'extensionFilter',
  'createdUserFilter', 'topicFilter', 'recordTopic', 'recordReferenceId', 'createdFromMillis', 'createdToMillis'
]

/** RN32 — is there an active search criterion (as opposed to just paging/sorting)? */
export const hasActiveCriteria = (criteria) => CRITERIA_FIELDS.some((field) => criteria[field] !== DEFAULT_CRITERIA[field])
